"use client";

import ScrollReveal from "@/components/common/ScrollReveal";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

type Scenario = {
  title: string;
  image: string;
  paragraphs: string[];
};

const SCENARIOS: Scenario[] = [
  {
    title: "External",
    image: "/images/service/offend/scenario-based-penetration-test/external.png",
    paragraphs: [
      "In External scenario, ECQ analyzes all possible attack vectors from an adversary point of view and execute all necessary hacking techniques to establish initial foothold to the target network.",
      "ECQ also simulates attacks originating from external branches or partners' network to help an organization understand better the threats that are coming from a compromised system of a remote branch or third-party vendor.",
    ],
  },
  {
    title: "Internal",
    image: "/images/service/offend/scenario-based-penetration-test/internal.png",
    paragraphs: [
      "Internal scenario penetration test helps an organization evaluate the risks of having a compromised system in the internal network and validate the effectiveness of internal security controls such as SOC/EDR/IDS.",
      "In this scenario, ECQ uses its laptop to connect to any LAN point or WiFi network provided to attack and perform lateral movement. The objectives here are to gain administrative controls of the Domain Controller and breach mission critical systems of the target organization.",
    ],
  },
  {
    title: "Credential",
    image: "/images/service/offend/scenario-based-penetration-test/credential.png",
    paragraphs: [
      "Credential or Valid Account scenario penetration test requires the Consultant to use a valid domain credential in an internal security assessment to try infiltrating deeper to the mission critical network segments via any LAN point or WiFi provided.",
      "The objective of this scenario is to help an organization discover insecure trusts, excessive privileges, leakage of sensitive information, and weak security controls.",
    ],
  },
  {
    title: "Workstation",
    image: "/images/service/offend/scenario-based-penetration-test/workstation.png",
    paragraphs: [
      "In this final scenario, ECQ tries to gain local access to the laptop or workstation provided; and if successful, escalate privilege to become the local administrator. This machine is then used to test installing unauthorized software and bypassing internal security controls.",
      "This type of assessment helps evaluate security controls and policy such as Network Access Control, removable devices policy, and data leakage. Workstation scenario penetration test illustrates the potential impact of having a system stolen or compromised.",
    ],
  },
];

/**
 * One scenario column.
 *
 * Below lg the card is a compact stacked block: icon + title on one row, body text
 * clamped with a fade and expandable via "Read more". Its background is #1C1E23
 * rather than the mockup's #15161A because this page sits on #15181D — the darker
 * value would be invisible against it.
 * From lg up it is the v1 four-column layout: title above, icon overlapping the
 * card top edge, full text always visible (no toggle).
 */
const ScenarioCard = ({ scenario, index }: { scenario: Scenario; index: number }) => {
  const [open, setOpen] = useState(false);
  // Assume the text overflows on mobile until measured, so the button doesn't pop in.
  const [clampable, setClampable] = useState(true);
  const [fullHeight, setFullHeight] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Read inside the ResizeObserver callback without re-subscribing on toggle.
  const openRef = useRef(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const measure = () => {
      setFullHeight(el.scrollHeight);
      // Once expanded scrollHeight === clientHeight, so only re-evaluate while collapsed.
      if (!openRef.current) setClampable(el.scrollHeight > el.clientHeight + 4);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggle = useCallback(() => {
    const el = bodyRef.current;
    if (el) setFullHeight(el.scrollHeight);
    openRef.current = !openRef.current;
    setOpen(openRef.current);
  }, []);

  const bodyId = `scenario-body-${index}`;

  return (
    <article className="scn-card relative z-[2] flex h-full flex-col rounded-[16px] bg-[#1C1E23] p-4 lg:block lg:rounded-none lg:bg-transparent lg:p-0">
      {/* Desktop-only card background: starts mid-icon (~179px) so the title
          sits above it; separate cards leave a visible gap. */}
      <div
        aria-hidden
        className="hidden lg:block absolute inset-x-0 top-[179px] bottom-0 rounded-[16px] bg-[linear-gradient(180deg,#090B0D_0%,#13161B_20%,#13161B_100%)]"
      />
      <div className="relative z-[1] flex h-full flex-col">
        {/* Head: icon beside the title on mobile, stacked (title first) from lg up */}
        <div className="flex items-center gap-[14px] lg:flex-col-reverse lg:gap-[21px]">
          <div className="relative w-20 h-20 shrink-0 lg:w-[193.748px] lg:h-[193.748px]">
            <Image
              src={scenario.image}
              alt={scenario.title}
              width={194}
              height={194}
              className="w-full h-full object-contain"
              priority={index < 2}
            />
          </div>
          <h2 className="min-w-0 font-[var(--font-avenir)] font-bold text-white text-[22px] leading-[1.15] tracking-[-0.01em] lg:text-[41.223px] lg:leading-[62.865px] lg:tracking-normal lg:text-center whitespace-nowrap">
            {scenario.title}
          </h2>
        </div>

        {/* Body: clamped with a bottom fade on mobile, always full from lg up */}
        <div
          ref={bodyRef}
          id={bodyId}
          style={
            open && fullHeight
              ? ({ "--scn-full": `${fullHeight}px` } as React.CSSProperties)
              : undefined
          }
          className={[
            "w-full mt-[14px] overflow-hidden transition-[max-height] duration-300 ease-[cubic-bezier(.4,0,.2,1)]",
            "lg:mt-[21px] lg:px-[24px] lg:max-h-none lg:overflow-visible lg:[mask-image:none] lg:[-webkit-mask-image:none]",
            open
              ? "max-h-[var(--scn-full)]"
              : "max-h-[9.2em] [mask-image:linear-gradient(#000_60%,transparent)] [-webkit-mask-image:linear-gradient(#000_60%,transparent)]",
          ].join(" ")}
        >
          <div className="flex flex-col gap-6 sm:gap-7 text-left text-text-highlight lg:text-justify lg:pb-10">
            {scenario.paragraphs.map((paragraph, i) => (
              <p
                key={i}
                className="font-[var(--font-avenir)] text-[15.5px] leading-[1.65] tracking-[0.5px] lg:text-[16px] lg:leading-[28px]"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {clampable && (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={bodyId}
            className="lg:hidden self-start inline-flex items-center gap-1.5 min-h-[44px] pt-3 pb-0.5 font-[var(--font-avenir)] font-semibold text-[14px] text-primary"
          >
            {open ? "Show less" : "Read more"}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className={`w-3.5 h-3.5 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        )}
      </div>
    </article>
  );
};

const ScenariosBasedPentrationTest = () => {
  return (
    <div className="relative w-full pt-2">
      {/* Intro description */}
      <section className="w-full px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1300px] mx-auto pb-12">
          <ScrollReveal direction="up" delay={0}>
            <div className="flex flex-col gap-6 sm:gap-7 max-w-[530px] text-text-highlight">
              <p className="font-[var(--font-avenir)] text-[16px] leading-[28px] tracking-[0.5px]">
                Penetration test service normally focuses on specific asset or target such as
                web/mobile application and doesn&apos;t cover assessment of the IT infrastructure of
                an organization. For this reason, penetration test alone cannot provide extensive
                visibility of security threats that are coming from many different angles.
              </p>
              <p className="font-[var(--font-avenir)] text-[16px] leading-[28px] tracking-[0.5px]">
                Scenario-based Penetration Test service provided by ECQ helps address the issue of
                limited threat visibility by simulating attacks from an external, internal, and
                rogue user point of view. Utilizing the same Tactics, Techniques, and Procedures as
                of real world advesaries or APT attackers, Scenario-based Penetration Test can be
                thought of as a lighter approach to Adversary Simulation where attacks are conducted
                based on each specific scenarios and require
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Scenarios: stacked expandable cards on mobile, four v1-style columns from lg up. */}
      <section className="relative w-full pb-16 px-4 sm:px-6 lg:px-6">
        <div className="relative w-full">
          <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2 sm:gap-x-6 sm:gap-y-4 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-12 items-stretch">
            {SCENARIOS.map((scenario, index) => (
              <ScrollReveal
                key={scenario.title}
                direction="up"
                delay={index * 100}
                className="h-full"
              >
                <ScenarioCard scenario={scenario} index={index} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScenariosBasedPentrationTest;
